import {
  MarketLandscapePackSizeData,
  MarketLandscapeRequestParams,
} from 'Interfaces/ProductWikiInterfaces/marketLandscape.types';
import { ChartOptions } from 'chart.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import { DateConversionUtility } from 'utils/dateUtilities';
import { PeriodOption } from './MarketLandscapeTab.type';

const useMarketLandscapeTab = (productAnalysisStore: ProductAnalysisStore) => {
  const [searchParams] = useSearchParams();
  const [activeView, setActiveView] = useState<'government' | 'commercial'>('government');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('3 months');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [showCustomDateModal, setShowCustomDateModal] = useState<boolean>(false);
  const [tempFromDate, setTempFromDate] = useState<Date | null>(null);
  const [tempToDate, setTempToDate] = useState<Date | null>(null);
  const [previousPeriod, setPreviousPeriod] = useState<string>('3 months');
  // Trigger counter to force re-fetch when user explicitly selects a period (even if it's the same)
  const [fetchTrigger, setFetchTrigger] = useState<number>(0);
  // Local loading state to show loader immediately when period changes (before store's loading state is set)
  const [isPendingFetch, setIsPendingFetch] = useState<boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track if a fetch is currently in progress to prevent duplicate calls
  const isFetchInProgressRef = useRef<boolean>(false);

  // Track if initial fetch is in progress or done for this component instance (prevents race conditions)
  const isMarketLandscapeFetchingOrFetchedRef = useRef<boolean>(false);
  // Track the GFC for which market landscape data was fetched
  const marketLandscapeFetchedGfcRef = useRef<string | null>(null);
  // Track the period for which market landscape data was fetched
  const marketLandscapeFetchedPeriodRef = useRef<string | null>(null);
  // Track the period we're currently waiting for (to prevent premature loader reset)
  const pendingFetchPeriodRef = useRef<string | null>(null);
  // Track previous loading state to detect when loading transitions from true to false
  const prevLoadingStateRef = useRef<boolean>(false);
  const demandCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // AI Insights - use store state directly (store handles duplicate prevention)
  const insightsAbortControllerRef = useRef<AbortController | null>(null);

  // Get GFC from URL params
  const gfc = searchParams.get('gfc');

  const periodOptions: PeriodOption[] = [
    { label: '3 months', value: '3 months' },
    { label: '6 months', value: '6 months' },
    { label: '12 months', value: '12 months' },
    { label: 'Custom', value: 'custom' },
  ];

  // Initialize prevLoadingStateRef with current loading state on mount
  useEffect(() => {
    prevLoadingStateRef.current = productAnalysisStore.isMarketLandscapeLoading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync selectedPeriod with store's currentPeriod (used by OpportunityHero dropdown)
  // This hook handles all market landscape fetching - store's updatePeriodAndFetch does not fetch it
  useEffect(() => {
    if (
      productAnalysisStore.currentPeriod &&
      productAnalysisStore.currentPeriod !== selectedPeriod
    ) {
      const newPeriod =
        productAnalysisStore.currentPeriod === 'custom'
          ? 'custom'
          : productAnalysisStore.currentPeriod;
      // Set pending fetch state BEFORE updating selectedPeriod to ensure loader shows immediately
      setIsPendingFetch(true);
      pendingFetchPeriodRef.current = newPeriod; // Track the period we're waiting for
      // Reset prevLoadingStateRef to ensure we detect the loading transition correctly
      prevLoadingStateRef.current = productAnalysisStore.isMarketLandscapeLoading;
      setSelectedPeriod(productAnalysisStore.currentPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productAnalysisStore.currentPeriod, selectedPeriod]);

  // Sync dates with store's currentFromDate and currentToDate
  useEffect(() => {
    if (productAnalysisStore.currentFromDate) {
      const parsedDate = DateConversionUtility.convertDBDateToLocal(
        productAnalysisStore.currentFromDate
      );
      if (parsedDate && parsedDate.getTime() !== fromDate?.getTime()) {
        setFromDate(parsedDate);
      }
    }
    if (productAnalysisStore.currentToDate) {
      const parsedDate = DateConversionUtility.convertDBDateToLocal(
        productAnalysisStore.currentToDate
      );
      if (parsedDate && parsedDate.getTime() !== toDate?.getTime()) {
        setToDate(parsedDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productAnalysisStore.currentFromDate, productAnalysisStore.currentToDate]);

  // Handle period selection from dropdown - always triggers a fetch
  const handlePeriodChange = useCallback(
    (periodValue: string) => {
      if (periodValue === 'custom') {
        setPreviousPeriod(selectedPeriod);
        setShowCustomDateModal(true);
        setTempFromDate(fromDate);
        setTempToDate(toDate);
        setSelectedPeriod('custom');
      } else {
        setIsPendingFetch(true); // Show loader immediately when period changes
        pendingFetchPeriodRef.current = periodValue; // Track the period we're waiting for
        // If selecting the same period, increment trigger to force re-fetch
        if (periodValue === selectedPeriod) {
          setFetchTrigger(prev => prev + 1);
        } else {
          setSelectedPeriod(periodValue);
        }
        setFromDate(null);
        setToDate(null);
        setShowCustomDateModal(false);
      }
    },
    [selectedPeriod, fromDate, toDate]
  );

  // Fetch market landscape data when GFC or date period changes
  // Note: Don't fetch when custom is selected until Apply is clicked (when fromDate and toDate are set)
  // This hook is the single source of truth for market landscape fetching
  useEffect(() => {
    if (!gfc || gfc.trim() === '') {
      return;
    }

    // Skip API call if custom period is selected but dates are not set yet
    if (selectedPeriod === 'custom' && (!fromDate || !toDate)) {
      return;
    }

    const trimmedGfc = gfc.trim();
    const currentPeriod = selectedPeriod === 'custom' ? 'custom' : selectedPeriod;

    // Skip fetch if already fetching/fetched for the same GFC and period (prevents race conditions)
    // Only skip on initial load - fetchTrigger > 0 means user explicitly requested refresh
    const isInitialLoad = fetchTrigger === 0;
    const isSameRequest =
      marketLandscapeFetchedGfcRef.current === trimmedGfc &&
      marketLandscapeFetchedPeriodRef.current === currentPeriod &&
      isMarketLandscapeFetchingOrFetchedRef.current;

    // Prevent duplicate calls: if we're already fetching for this exact period, skip
    // UNLESS fetchTrigger > 0 (user explicitly requested refresh) OR pendingFetchPeriodRef indicates a new period
    const isAlreadyFetchingSamePeriod =
      isMarketLandscapeFetchingOrFetchedRef.current &&
      marketLandscapeFetchedGfcRef.current === trimmedGfc &&
      marketLandscapeFetchedPeriodRef.current === currentPeriod &&
      pendingFetchPeriodRef.current === currentPeriod;

    if (isInitialLoad && isSameRequest) {
      setIsPendingFetch(false);
      pendingFetchPeriodRef.current = null; // Clear pending period since we're not fetching
      return;
    }

    // Prevent duplicate API calls: if we're already fetching for this period and it's not a user-triggered refresh
    if (isAlreadyFetchingSamePeriod && fetchTrigger === 0) {
      return;
    }

    // Prevent duplicate calls: if a fetch is already in progress for this exact period/GFC combo
    // BUT allow if pendingFetchPeriodRef indicates we're waiting for a different period (new period change)
    if (
      isFetchInProgressRef.current &&
      marketLandscapeFetchedGfcRef.current === trimmedGfc &&
      marketLandscapeFetchedPeriodRef.current === currentPeriod &&
      pendingFetchPeriodRef.current === currentPeriod &&
      fetchTrigger === 0
    ) {
      return;
    }

    // If we're fetching for a different period, reset the flag (we're aborting the previous fetch)
    if (isFetchInProgressRef.current && marketLandscapeFetchedPeriodRef.current !== currentPeriod) {
      isFetchInProgressRef.current = false;
    }

    // Mark as fetching BEFORE starting async operation (prevents React StrictMode double calls)
    isFetchInProgressRef.current = true;
    isMarketLandscapeFetchingOrFetchedRef.current = true;
    marketLandscapeFetchedGfcRef.current = trimmedGfc;
    marketLandscapeFetchedPeriodRef.current = currentPeriod;
    // Track the period we're currently fetching for (ensure it's set before fetch starts)
    pendingFetchPeriodRef.current = currentPeriod;
    // Ensure loading state is tracked correctly
    prevLoadingStateRef.current = productAnalysisStore.isMarketLandscapeLoading;

    // Abort previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const params: { gfc: string; period?: string; from_date?: string; to_date?: string } = {
      gfc: trimmedGfc,
    };

    if (selectedPeriod === 'custom' && fromDate && toDate) {
      params.period = 'custom';
      params.from_date = DateConversionUtility.convertLocalToDBDateString(fromDate);
      params.to_date = DateConversionUtility.convertLocalToDBDateString(toDate);
    } else if (selectedPeriod !== 'custom') {
      params.period = selectedPeriod;
    } else {
      // Default to 3 months if custom selected but dates not set (shouldn't reach here due to early return)
      params.period = '3 months';
    }

    // Call fetchMarketLandscape (async but we don't await - completion tracked via store loading state)
    productAnalysisStore.fetchMarketLandscape(
      params as MarketLandscapeRequestParams,
      abortController.signal
    );

    // Cleanup: abort request when effect re-runs or component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        // Reset fetch in progress flag when request is aborted
        isFetchInProgressRef.current = false;
      }
      // Reset the flag if request was aborted so it can be retried on the next mount
      // Only reset if the abort was for this same GFC/period combo
      if (
        marketLandscapeFetchedGfcRef.current === trimmedGfc &&
        marketLandscapeFetchedPeriodRef.current === currentPeriod
      ) {
        // Don't reset if data was successfully loaded
        if (!productAnalysisStore.marketLandscapeData) {
          isMarketLandscapeFetchingOrFetchedRef.current = false;
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gfc, selectedPeriod, fromDate, toDate, fetchTrigger]);

  // Reset pending fetch state ONLY when store's loading completes AND data matches the period we're waiting for
  useEffect(() => {
    const currentPeriod = selectedPeriod === 'custom' ? 'custom' : selectedPeriod;
    const isWaitingForThisPeriod = pendingFetchPeriodRef.current === currentPeriod;
    const isLoading = productAnalysisStore.isMarketLandscapeLoading;
    const hasData = !!productAnalysisStore.marketLandscapeData;

    // Track when loading transitions from true to false (fetch just completed)
    const loadingJustCompleted = prevLoadingStateRef.current === true && isLoading === false;

    // Update previous loading state for next comparison
    prevLoadingStateRef.current = isLoading;

    // Reset fetch in progress flag when loading completes
    if (loadingJustCompleted) {
      isFetchInProgressRef.current = false;
    }

    // Only reset isPendingFetch when:
    // 1. Loading just completed (transitioned from true to false)
    // 2. Data exists
    // 3. We're waiting for this specific period
    if (loadingJustCompleted && hasData && isWaitingForThisPeriod) {
      setIsPendingFetch(false);
      // Clear the pending period ref after resetting
      pendingFetchPeriodRef.current = null;
    }

    // Also reset if we're not waiting for any period (safety check)
    if (!pendingFetchPeriodRef.current && isLoading === false) {
      setIsPendingFetch(false);
    }
  }, [
    productAnalysisStore.isMarketLandscapeLoading,
    productAnalysisStore.marketLandscapeData,
    selectedPeriod,
  ]);

  // Fetch AI Insights only on initial load (when GFC is available)
  // This should NOT be triggered by period changes since insights use a fixed 12-month period
  // Store handles duplicate prevention, so multiple hook instances won't cause duplicate calls
  useEffect(() => {
    if (!gfc || gfc.trim() === '') {
      return;
    }

    const trimmedGfc = gfc.trim();

    // Abort previous insights request if it exists
    if (insightsAbortControllerRef.current) {
      insightsAbortControllerRef.current.abort();
      insightsAbortControllerRef.current = null;
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    insightsAbortControllerRef.current = abortController;

    // Use store-level fetching (store prevents duplicates)
    productAnalysisStore.fetchAIInsights(
      {
        insight_type: 'product_overview',
        gfc: trimmedGfc,
        period_option: '12 months',
        llm_backend: 'anthropic',
      },
      abortController.signal
    );

    // Cleanup: abort request when effect re-runs or component unmounts
    return () => {
      if (insightsAbortControllerRef.current) {
        insightsAbortControllerRef.current.abort();
        insightsAbortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gfc]); // Only depend on gfc - insights are fetched once on initial load (store prevents duplicates)

  // Get data for "All" pack sizes (aggregated data) for the active view (government or commercial)
  // Handle both new nested structure (data[packSize][government/commercial]) and old flat structure (data[packSize])
  const allPackSizeData = productAnalysisStore.marketLandscapeData?.data?.['All'];
  const currentPackSizeData: MarketLandscapePackSizeData | null =
    allPackSizeData && typeof allPackSizeData === 'object' && 'government' in allPackSizeData
      ? allPackSizeData[activeView] || null // New nested structure
      : activeView === 'government' && allPackSizeData
        ? (allPackSizeData as unknown as MarketLandscapePackSizeData) // Old flat structure (assume government)
        : null; // Commercial not available in old structure

  // Prepare Chart.js data for Market Share Bar Chart (Top 5 manufacturers only)
  // Using single dataset with multiple colors for even bar spacing (matching Current Price per Labeler style)
  const marketShareChartData = useMemo(() => {
    if (
      !currentPackSizeData ||
      !currentPackSizeData.marketShare ||
      currentPackSizeData.marketShare.length === 0
    ) {
      return null;
    }

    // Sort by percentage descending and take top 5 manufacturers
    const sortedData = [...currentPackSizeData.marketShare]
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 5);

    // Use manufacturer names as x-axis labels
    const labels = sortedData.map(item => item.name || item.manufacturer || 'Unknown');

    // Extract percentages and colors for each manufacturer
    // Use same color for all bars (Brand Purple)
    const percentages = sortedData.map(item => item.percentage || 0);
    const backgroundColors = '#522D6E';
    const borderColors = '#522D6E';

    // Single dataset with all values - creates evenly spaced bars
    return {
      labels,
      datasets: [
        {
          label: 'Market Share (%)',
          data: percentages,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 2, // Slightly thicker border for better definition
          borderRadius: 4, // Rounded bar corners like Current Price per Labeler
          hoverBackgroundColor: borderColors, // Solid color on hover
          hoverBorderColor: borderColors,
          hoverBorderWidth: 2,
        },
      ],
    };
  }, [currentPackSizeData]);

  // Chart.js options for Market Share Bar Chart (styled to match Current Price per Labeler)
  const marketShareChartOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      indexAxis: 'x' as const, // Vertical bars - manufacturers on X-axis, percentages on Y-axis
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false, // Hide legend since manufacturer names are on x-axis
        },
        title: {
          display: false, // Title is shown as external header
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#374151',
          bodyColor: '#374151',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          padding: 12,
          callbacks: {
            label: function (context) {
              const value = context.parsed.y ?? 0;
              return `Market Share: ${value.toFixed(1)}%`;
            },
          },
        },
      },
      scales: {
        x: {
          type: 'category' as const,
          title: {
            display: true,
            text: 'Manufacturer',
            color: '#374151',
            font: {
              size: 15,
              weight: 'bold' as const,
            },
          },
          ticks: {
            color: '#374151',
            font: {
              size: 15,
            },
            maxRotation: 45,
            minRotation: 0,
            autoSkip: false,
            callback: function (value, _index, _ticks) {
              // Get the label from the tick
              const label = this.getLabelForValue(value as number);
              // Truncate to first 5 characters
              if (typeof label === 'string' && label.length > 5) {
                return label.substring(0, 5) + '...';
              }
              return label;
            },
          },
          grid: {
            display: false,
          },
        },
        y: {
          type: 'linear' as const,
          title: {
            display: true,
            text: 'Market Share (%)',
            color: '#374151',
            font: {
              size: 15,
              weight: 'bold' as const,
            },
          },
          beginAtZero: true,
          max: 100,
          ticks: {
            color: '#374151',
            font: {
              size: 15,
            },
            callback: function (value) {
              return `${value}%`;
            },
          },
          grid: {
            color: '#e5e7eb',
          },
        },
      },
    }),
    []
  );

  // Show loading when: store is loading, OR we're pending a fetch (period just changed)
  // Ensure loader stays visible as long as store is loading OR we're waiting for specific period data
  const isLoading =
    productAnalysisStore.isMarketLandscapeLoading ||
    (isPendingFetch && pendingFetchPeriodRef.current !== null);
  const hasError = !!productAnalysisStore.marketLandscapeError;

  // Generate top manufacturers description (sorted by percentage descending)
  const getTopManufacturersText = () => {
    if (
      !currentPackSizeData ||
      !currentPackSizeData.marketShare ||
      currentPackSizeData.marketShare.length === 0
    ) {
      return 'No data available';
    }

    // Sort by percentage descending and take top 5
    const top5 = [...currentPackSizeData.marketShare]
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 5);
    return top5.map(item => `${item.name} (${item.percentage}%)`).join(', ');
  };

  // Check if current view data is empty (no market share data)
  const hasNoData =
    !currentPackSizeData ||
    !currentPackSizeData.marketShare ||
    currentPackSizeData.marketShare.length === 0;

  return {
    // State
    activeView,
    setActiveView,
    selectedPeriod,
    setSelectedPeriod,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    showCustomDateModal,
    setShowCustomDateModal,
    tempFromDate,
    setTempFromDate,
    tempToDate,
    setTempToDate,
    previousPeriod,
    setPreviousPeriod,

    // Data
    currentPackSizeData,
    isLoading,
    hasError,
    hasNoData,
    errorMessage: productAnalysisStore.marketLandscapeError,

    // AI Insights - use store state directly
    aiInsights: productAnalysisStore.aiInsights,
    isInsightsLoading: productAnalysisStore.isAIInsightsLoading,
    insightsError: productAnalysisStore.aiInsightsError,

    // Chart data
    marketShareChartData,
    marketShareChartOptions,
    demandCanvasRef,

    // Options
    periodOptions,

    // Utility functions
    getTopManufacturersText,

    // Event handlers
    handlePeriodChange,
  };
};

export default useMarketLandscapeTab;
